<template>
  <v-container fluid>
    <!-- Encabezado -->
    <v-row align="center" justify="space-between" class="mb-4">
      <v-col cols="12" md="8" class="d-flex align-center">
        <h2 class="text-h5 font-weight-bold">
          {{ esEdicion ? 'Editar receta / programa' : 'Nueva receta / programa' }}
        </h2>
        <v-chip
          :color="esCombinada ? 'deep-purple' : 'blue-grey lighten-3'"
          :dark="esCombinada"
          small
          class="ml-3"
        >
          <v-icon x-small left>{{ esCombinada ? 'mdi-set-merge' : 'mdi-square-outline' }}</v-icon>
          {{ esCombinada ? 'Combinada' : 'Simple' }}
        </v-chip>
      </v-col>
      <v-col cols="12" md="4" class="text-md-right">
        <v-btn text color="primary" @click="$router.back()">
          <v-icon left>mdi-arrow-left</v-icon>
          Volver
        </v-btn>
      </v-col>
    </v-row>

    <v-row v-if="cargando">
      <v-col cols="12"><v-skeleton-loader type="article, article" /></v-col>
    </v-row>

    <v-row v-else>
      <!-- Datos generales -->
      <v-col cols="12" md="4">
        <v-card class="mb-4">
          <v-card-title class="subtitle-1 font-weight-bold">Datos generales</v-card-title>
          <v-card-text>
            <v-text-field
              v-model="nombre"
              outlined
              dense
              label="Nombre"
              placeholder="Ej: Placa fibrofácil 18mm → bandejas"
              :error-messages="errores.nombre"
              @input="errores.nombre = ''"
            />
            <v-select
              v-model="maquina"
              :items="maquinas"
              item-text="nombre"
              item-value="id"
              outlined
              dense
              label="Máquina"
              clearable
              hint="Opcional. Multiplica el costo/min por el tiempo de proceso."
              persistent-hint
            />
            <v-text-field
              v-model.number="tiempoMinutos"
              type="number"
              min="0"
              step="0.5"
              outlined
              dense
              class="mt-3"
              label="Tiempo de proceso (min por lote)"
              suffix="min"
            />
            <v-textarea
              v-model="observaciones"
              outlined
              dense
              rows="3"
              class="mt-1"
              label="Observaciones"
              placeholder="Opcional"
            />
            <v-switch
              v-if="esEdicion"
              v-model="activa"
              inset
              dense
              color="green"
              hide-details
              class="mt-1"
              :label="activa ? 'Receta activa' : 'Receta inactiva'"
            />
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Consume / Produce -->
      <v-col cols="12" md="8">
        <!-- CONSUME -->
        <v-card class="mb-4">
          <v-card-title class="subtitle-1 font-weight-bold red--text text--darken-1">
            <v-icon left color="red darken-1">mdi-tray-arrow-down</v-icon>
            Consume (insumos por lote)
          </v-card-title>
          <v-card-text>
            <v-autocomplete
              v-model="consumoSel"
              :items="consumoItems"
              :loading="loadingConsumo"
              :search-input.sync="consumoSearch"
              item-text="descripcion"
              return-object
              no-filter
              hide-no-data
              clearable
              outlined
              dense
              prepend-inner-icon="mdi-magnify"
              label="Agregar insumo..."
              placeholder="Escribí para buscar"
              @change="agregar('consumos', $event)"
            />

            <v-simple-table v-if="consumos.length" dense>
              <thead>
                <tr>
                  <th>Insumo</th>
                  <th class="text-right" style="width: 140px">Cantidad</th>
                  <th style="width: 48px"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(c, i) in consumos" :key="'c' + i">
                  <td>{{ c.descripcion }}</td>
                  <td class="text-right">
                    <v-text-field
                      v-model.number="c.cantidad"
                      type="number"
                      min="0"
                      step="0.01"
                      dense
                      outlined
                      hide-details
                      class="ml-auto"
                      style="width: 110px"
                    />
                  </td>
                  <td>
                    <v-btn icon small color="error" @click="quitar('consumos', i)">
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
            <v-alert v-else type="info" outlined dense class="mb-0 caption">
              Buscá y agregá los insumos que consume cada lote.
            </v-alert>
          </v-card-text>
        </v-card>

        <!-- PRODUCE -->
        <v-card>
          <v-card-title class="subtitle-1 font-weight-bold green--text text--darken-1">
            <v-icon left color="green darken-1">mdi-tray-arrow-up</v-icon>
            Produce (salidas por lote)
            <v-spacer />
            <v-btn
              v-if="esCombinada"
              x-small
              text
              color="primary"
              @click="repartirEquitativo"
            >
              <v-icon x-small left>mdi-scale-balance</v-icon>
              Repartir equitativo
            </v-btn>
          </v-card-title>
          <v-card-text>
            <v-autocomplete
              v-model="salidaSel"
              :items="salidaItems"
              :loading="loadingSalida"
              :search-input.sync="salidaSearch"
              item-text="descripcion"
              return-object
              no-filter
              hide-no-data
              clearable
              outlined
              dense
              prepend-inner-icon="mdi-magnify"
              label="Agregar producto que sale..."
              placeholder="Escribí para buscar"
              @change="agregar('salidas', $event)"
            />

            <v-simple-table v-if="salidas.length" dense>
              <thead>
                <tr>
                  <th>Producto</th>
                  <th class="text-right" style="width: 130px">Cantidad</th>
                  <th class="text-right" style="width: 120px">% costo</th>
                  <th style="width: 48px"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(s, i) in salidas" :key="'s' + i">
                  <td>
                    <v-icon
                      v-if="esCombinada && i === salidaPrincipalIndex"
                      x-small color="amber darken-2" class="mr-1"
                    >mdi-star</v-icon>
                    {{ s.descripcion }}
                  </td>
                  <td class="text-right">
                    <v-text-field
                      v-model.number="s.cantidad"
                      type="number"
                      min="0"
                      step="0.01"
                      dense
                      outlined
                      hide-details
                      class="ml-auto"
                      style="width: 100px"
                    />
                  </td>
                  <td class="text-right">
                    <v-text-field
                      v-model.number="s.proporcion_costo"
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      dense
                      outlined
                      hide-details
                      suffix="%"
                      class="ml-auto"
                      style="width: 90px"
                    />
                  </td>
                  <td>
                    <v-btn icon small color="error" @click="quitar('salidas', i)">
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td colspan="2" class="text-right font-weight-medium">Total % costo asignado</td>
                  <td class="text-right font-weight-bold" :class="totalClass">{{ totalProporcion }}%</td>
                  <td></td>
                </tr>
              </tfoot>
            </v-simple-table>
            <v-alert v-else type="info" outlined dense class="mb-0 caption">
              Buscá y agregá los productos que salen. Con más de uno, la receta se marca
              automáticamente como <strong>combinada</strong>.
            </v-alert>

            <v-alert
              v-if="salidas.length && totalProporcion > 100"
              type="error" text dense class="mt-3 mb-0 caption"
            >
              El % de costo asignado supera el 100%. Ajustá los valores antes de guardar.
            </v-alert>
            <v-alert
              v-else-if="salidas.length && totalProporcion < 100"
              type="warning" text dense class="mt-3 mb-0 caption"
            >
              El {{ (100 - totalProporcion).toFixed(0) }}% restante se considera merma (no se
              carga a ningún producto).
            </v-alert>
          </v-card-text>
        </v-card>

        <div class="d-flex justify-end mt-4">
          <v-btn
            color="deep-purple"
            dark
            :disabled="!puedeGuardar"
            :loading="guardando"
            @click="guardar"
          >
            <v-icon left>mdi-content-save</v-icon>
            {{ esEdicion ? 'Guardar cambios' : 'Crear receta' }}
          </v-btn>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex';

export default {
  name: 'RecetaRegistrar',
  data() {
    return {
      cargando: false,
      guardando: false,

      maquinas: [],

      // Campos de la receta
      nombre: '',
      maquina: null,
      tiempoMinutos: 0,
      observaciones: '',
      activa: true,
      consumos: [],
      salidas: [],

      // Buscadores
      consumoSel: null,
      consumoSearch: '',
      consumoItems: [],
      loadingConsumo: false,
      salidaSel: null,
      salidaSearch: '',
      salidaItems: [],
      loadingSalida: false,

      errores: { nombre: '' },
      _debConsumo: null,
      _debSalida: null,
    };
  },
  computed: {
    esEdicion() {
      return !!this.$route.params.id;
    },
    esCombinada() {
      return this.salidas.length > 1;
    },
    salidaPrincipalIndex() {
      // La salida con mayor % de costo es la principal.
      let idx = 0;
      let max = -Infinity;
      this.salidas.forEach((s, i) => {
        const p = parseFloat(s.proporcion_costo) || 0;
        if (p > max) { max = p; idx = i; }
      });
      return idx;
    },
    totalProporcion() {
      const total = this.salidas.reduce(
        (acc, s) => acc + (parseFloat(s.proporcion_costo) || 0), 0,
      );
      return Math.round(total * 100) / 100;
    },
    totalClass() {
      if (this.totalProporcion > 100) return 'red--text';
      if (this.totalProporcion < 100) return 'orange--text';
      return 'green--text';
    },
    puedeGuardar() {
      return (
        this.nombre.trim().length > 0
        && this.consumos.length > 0
        && this.salidas.length > 0
        && this.totalProporcion <= 100
        && !this.guardando
      );
    },
  },
  watch: {
    consumoSearch(val) {
      this.buscar(val, 'consumo');
    },
    salidaSearch(val) {
      this.buscar(val, 'salida');
    },
  },
  methods: {
    ...mapActions('recetaProduccion', ['get_receta', 'create_receta', 'update_receta']),
    ...mapActions('maquinaProduccion', ['fetch_maquinas']),

    snack(color, text, icon) {
      this.$updateBus.$emit('showSnackBar', {
        show: true, button: false, color, text, icon, timeout: 3000,
      });
    },

    buscar(val, tipo) {
      const timerKey = tipo === 'consumo' ? '_debConsumo' : '_debSalida';
      const itemsKey = tipo === 'consumo' ? 'consumoItems' : 'salidaItems';
      const loadingKey = tipo === 'consumo' ? 'loadingConsumo' : 'loadingSalida';
      if (!val || val.length < 2) {
        this[itemsKey] = [];
        return;
      }
      clearTimeout(this[timerKey]);
      this[timerKey] = setTimeout(async () => {
        this[loadingKey] = true;
        try {
          const res = await this.axios.get(`/productos/suggest?q=${encodeURIComponent(val)}`);
          this[itemsKey] = res.data.results || [];
        } catch (e) {
          console.error(e);
        } finally {
          this[loadingKey] = false;
        }
      }, 300);
    },

    agregar(lista, producto) {
      if (!producto || !producto.psp_id) return;
      const arr = this[lista];
      const existente = arr.find((x) => x.psp === producto.psp_id);
      if (existente) {
        existente.cantidad = (parseFloat(existente.cantidad) || 0) + 1;
      } else if (lista === 'consumos') {
        arr.push({ psp: producto.psp_id, descripcion: producto.descripcion, cantidad: 1 });
      } else {
        arr.push({
          psp: producto.psp_id,
          descripcion: producto.descripcion,
          cantidad: 1,
          proporcion_costo: arr.length === 0 ? 100 : 0,
        });
      }
      this.$nextTick(() => {
        if (lista === 'consumos') {
          this.consumoSel = null;
          this.consumoSearch = '';
          this.consumoItems = [];
        } else {
          this.salidaSel = null;
          this.salidaSearch = '';
          this.salidaItems = [];
        }
      });
    },

    quitar(lista, index) {
      this[lista].splice(index, 1);
    },

    repartirEquitativo() {
      const n = this.salidas.length;
      if (!n) return;
      const base = Math.floor((100 / n) * 100) / 100;
      this.salidas.forEach((s, i) => {
        // El último absorbe el redondeo para cerrar en 100 exacto.
        s.proporcion_costo = i === n - 1
          ? Math.round((100 - base * (n - 1)) * 100) / 100
          : base;
      });
    },

    async cargarMaquinas() {
      try {
        const res = await this.fetch_maquinas();
        this.maquinas = res.data;
      } catch (e) {
        console.error(e);
      }
    },

    async cargarReceta() {
      this.cargando = true;
      try {
        const res = await this.get_receta(this.$route.params.id);
        const r = res.data;
        this.nombre = r.nombre;
        this.maquina = r.maquina;
        this.tiempoMinutos = parseFloat(r.tiempo_minutos) || 0;
        this.observaciones = r.observaciones || '';
        this.activa = r.activa;
        this.consumos = (r.consumos || []).map((c) => ({
          psp: c.psp, descripcion: c.descripcion, cantidad: parseFloat(c.cantidad),
        }));
        this.salidas = (r.salidas || []).map((s) => ({
          psp: s.psp,
          descripcion: s.descripcion,
          cantidad: parseFloat(s.cantidad),
          proporcion_costo: parseFloat(s.proporcion_costo),
        }));
      } catch (e) {
        console.error(e);
        this.snack('error', 'No se pudo cargar la receta', 'mdi-alert');
        this.$router.back();
      } finally {
        this.cargando = false;
      }
    },

    validar() {
      this.errores.nombre = '';
      if (!this.nombre.trim()) {
        this.errores.nombre = 'Ingresá un nombre';
        return false;
      }
      const cSinCant = this.consumos.some((c) => !(parseFloat(c.cantidad) > 0));
      const sSinCant = this.salidas.some((s) => !(parseFloat(s.cantidad) > 0));
      if (cSinCant || sSinCant) {
        this.snack('error', 'Todas las cantidades deben ser mayores a 0', 'mdi-alert');
        return false;
      }
      return true;
    },

    async guardar() {
      if (!this.validar()) return;
      this.guardando = true;
      const payload = {
        nombre: this.nombre.trim(),
        maquina: this.maquina || null,
        tiempo_minutos: this.tiempoMinutos || 0,
        observaciones: this.observaciones,
        activa: this.activa,
        consumos: this.consumos.map((c) => ({ psp: c.psp, cantidad: String(c.cantidad) })),
        salidas: this.salidas.map((s) => ({
          psp: s.psp,
          cantidad: String(s.cantidad),
          proporcion_costo: String(s.proporcion_costo),
        })),
      };
      try {
        let id = this.$route.params.id;
        if (this.esEdicion) {
          await this.update_receta({ id, data: payload });
        } else {
          const res = await this.create_receta(payload);
          id = res.data.id;
        }
        this.snack('success', this.esEdicion ? 'Receta actualizada' : 'Receta creada', 'mdi-check');
        this.$router.push({ name: 'empresa.produccion.receta.consultar', params: { id } });
      } catch (e) {
        const msg = e.response?.data?.error || 'Error al guardar la receta';
        this.snack('error', msg, 'mdi-alert');
      } finally {
        this.guardando = false;
      }
    },
  },
  created() {
    this.cargarMaquinas();
    if (this.esEdicion) this.cargarReceta();
  },
};
</script>
